import React, { useState } from 'react';
import { shouldShowAppleSignIn } from '../auth/socialSignIn';
import { ReactComponent as ShowPassIcon } from '../assets/icons/show_pass.svg';
import { ReactComponent as HidePassIcon } from '../assets/icons/hide_pass.svg';

export function errorText(error, fallback) {
  const data = error && error.response && error.response.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.error === 'string') return data.error;
  if (Array.isArray(data.non_field_errors) && data.non_field_errors[0]) {
    return String(data.non_field_errors[0]);
  }
  const key = Object.keys(data)[0];
  if (!key) return fallback;
  const value = data[key];
  if (Array.isArray(value) && value[0]) return String(value[0]);
  if (typeof value === 'string') return value;
  return fallback;
}

export function fieldText(value) {
  if (!value) return '';
  if (Array.isArray(value)) return String(value[0] || '');
  return String(value);
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg width="16" height="18" viewBox="0 0 16 18" aria-hidden="true">
      <path
        fill="#111"
        d="M13.05 9.47c-.03-2.32 1.9-3.43 1.98-3.49-1.08-1.58-2.76-1.8-3.36-1.82-1.43-.15-2.79.84-3.52.84-.72 0-1.84-.82-3.03-.8-1.56.03-3 .91-3.8 2.3-1.62 2.81-.41 6.98 1.16 9.26.77 1.12 1.69 2.37 2.9 2.32 1.16-.05 1.6-.75 3-.75s1.8.75 3.03.73c1.25-.02 2.04-1.14 2.8-2.26.89-1.3 1.25-2.56 1.27-2.62-.03-.01-2.44-.94-2.47-3.71zM10.9 2.9c.64-.77 1.07-1.85.95-2.92-.92.04-2.03.61-2.69 1.38-.59.68-1.1 1.77-.96 2.81 1.02.08 2.07-.52 2.7-1.27z"
      />
    </svg>
  );
}

export function AuthScreen({ brand, children }) {
  if (!brand.ready) {
    return <main className="auth-screen" />;
  }
  return (
    <main
      className="auth-screen"
      style={{
        '--auth-button': brand.buttonColor,
        '--auth-button-text': brand.buttonTextColor,
      }}
    >
      <section className="auth-card">{children}</section>
    </main>
  );
}

export function BrandHeader({ brand }) {
  if (brand.isSchool) {
    return (
      <div className="auth-school-brand">
        {brand.logoUrl ? (
          <img src={brand.logoUrl} alt="" className="auth-school-logo" />
        ) : null}
        <span className="auth-school-name">{brand.name}</span>
      </div>
    );
  }
  return <div className="auth-wordmark">HOMECROWD</div>;
}

export function PoweredBy() {
  return (
    <p className="auth-powered">
      Powered by <span>HOMECROWD</span>
    </p>
  );
}

export function SocialButtons({ onGoogle, onApple, disabled }) {
  return (
    <div className="auth-socials">
      <button type="button" className="auth-social" onClick={onGoogle} disabled={disabled}>
        <GoogleMark />
        Continue with Google
      </button>
      {shouldShowAppleSignIn() ? (
        <button type="button" className="auth-social" onClick={onApple} disabled={disabled}>
          <AppleMark />
          Continue with Apple
        </button>
      ) : null}
    </div>
  );
}

export function PasswordField({
  id,
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  labelExtra,
  error,
  hint,
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="auth-field">
      <div className="auth-label-row">
        <label className="auth-label" htmlFor={id}>
          {label}
        </label>
        {labelExtra}
      </div>
      <div className="auth-password">
        <input
          id={id}
          className="auth-input"
          type={visible ? 'text' : 'password'}
          name="password"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
        />
        <button
          type="button"
          className="auth-eye"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <HidePassIcon /> : <ShowPassIcon />}
        </button>
      </div>
      {error ? <p className="auth-field-error">{error}</p> : null}
      {hint ? <p className="auth-hint">{hint}</p> : null}
    </div>
  );
}
